import { useState, useEffect } from 'react';
import ContentCard from '../../../../../components/layout/ContentCard/ContentCard';
import Button from '../../../../../components/common/Button/Button';
import Input from '../../../../../components/common/Input/Input';
import Badge from '../../../../../components/common/Badge/Badge';
import { 
  translate, 
  verifyHideCountryItems, 
  getFormatDate,
  getFormatDateInput,
  copyToClipboard,
  getNameStatusClients,
  getNameGender,
  getNameRoleBli,
  getNameTipoPlanCliente
} from '../../../../../utils/Common';
import { FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiGlobe, FiKey, FiEye, FiEyeOff, FiInfo } from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { ImQrcode } from 'react-icons/im';
import QRCode from 'qrcode.react';
import ModalContent from '../../../../../components/ModalContent';
import { getStatusVariant } from '../../utils/clientHelpers';
import { getClientConfRoleInfo } from '../../../../../utils/modules/clients';
import styles from '../../ClientDetail.module.css';

const PersonalInfoTab = ({ client, onUpdateClient, formatClientData, onReloadClient, clientId, canUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAccessCode, setIsEditingAccessCode] = useState(false);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    phone: '',
    gender: '',
    birthDate: '',
    pais: '',
    estado: '',
    ciudad: '',
    address1: '',
    address2: '',
    externalId: '',
    accessCode: '',
    hashKey: '',
    key: '',
    acc: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isAccessible, setIsAccessible] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client?.Nombre || '',
        lastName: client?.Apellidos || '',
        phone: client?.Telefono || '',
        gender: client?.Sexo || '',
        birthDate: client?.FechaNacimiento ? getFormatDateInput(client.FechaNacimiento) : '',
        pais: client?.Pais || '',
        estado: client?.Estado || '',
        ciudad: client?.Ciudad || '',
        address1: client?.Address1 || '',
        address2: client?.Address2 || '',
        externalId: client?.UserIdGL || '',
        accessCode: client?.AccessCode || '',
        hashKey: client?.HashKey || '',
        key: client?.Clave || '',
        acc: client?.Acc || false,
      });
      setIsAccessible(client?.Acc || false);
    }
  }, [client]);

  if (!client) return null;

  const isDriver = Number(client?.IdConfRoles) === 2;
  const canEditAccessCode = isDriver && canUpdate;

  // Locker admin comes from an active admin assignment
  const isAdmin = Boolean(client?.IsLockerAdmin);
  const canEditKey = isAdmin && canUpdate;

  const toggleShowAccessCode = () => setShowAccessCode(!showAccessCode);
  const toggleShowKey = () => setShowKey(!showKey);

  const handleCopyClipboard = (text) => {
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      name: client?.Nombre || '',
      lastName: client?.Apellidos || '',
      phone: client?.Telefono || '',
      gender: client?.Sexo || '',
      birthDate: client?.FechaNacimiento ? getFormatDateInput(client.FechaNacimiento) : '',
      pais: client?.Pais || '',
      estado: client?.Estado || '',
      ciudad: client?.Ciudad || '',
      address1: client?.Address1 || '',
      address2: client?.Address2 || '',
      externalId: client?.UserIdGL || '',
      accessCode: client?.AccessCode || '',
      hashKey: client?.HashKey || '',
      acc: client?.Acc || false,
    });
    setIsAccessible(client?.Acc || false);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess(false);

    if (!formData.name.trim() || !formData.lastName.trim()) {
      setSaveError(translate('dashboard.clients.detail.overview.validation.nameRequired') || 'Name and last name are required');
      return;
    }

    setIsSaving(true);
    try {
      const formattedData = formatClientData ? formatClientData({
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        birthDate: formData.birthDate,
        pais: formData.pais.trim(),
        estado: formData.estado.trim(),
        ciudad: formData.ciudad.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        externalId: formData.externalId.trim(),
        accessCode: formData.accessCode.trim(),
        hashKey: formData.hashKey.trim(),
        acc: isAccessible,
      }) : {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        birthDate: formData.birthDate,
        pais: formData.pais.trim(),
        estado: formData.estado.trim(),
        ciudad: formData.ciudad.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        externalId: formData.externalId.trim(),
        accessCode: formData.accessCode.trim(),
        hashKey: formData.hashKey.trim(),
        acc: isAccessible,
      };

      const result = await onUpdateClient({
        typeUpdate: 'EDIT_INFO_CLIENT',
        ...formattedData
      });
      
      if (result.success) {
        setSaveSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onReloadClient) {
          await onReloadClient({ clientId });
        }
      } else {
        setSaveError(result.error || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditAccessCode = () => {
    setIsEditingAccessCode(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditAccessCode = () => {
    setIsEditingAccessCode(false);
    setFormData({ ...formData, accessCode: client?.AccessCode || '' });
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSaveAccessCode = async () => {
    setSaveError('');
    setSaveSuccess(false);

    // an empty save would blank the driver's access code on the backend
    if (!formData.accessCode.trim()) {
      setSaveError(translate('dashboard.clients.detail.overview.errors.emptyField') || 'This field cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        birthDate: formData.birthDate,
        pais: formData.pais.trim(),
        estado: formData.estado.trim(),
        ciudad: formData.ciudad.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        externalId: formData.externalId.trim(),
        accessCode: formData.accessCode.trim(),
        hashKey: formData.hashKey.trim(),
        acc: isAccessible,
      };
      const formattedData = formatClientData ? formatClientData(payload) : payload;

      const result = await onUpdateClient({
        typeUpdate: 'EDIT_INFO_CLIENT',
        ...formattedData
      });

      if (result.success) {
        setSaveSuccess(true);
        setIsEditingAccessCode(false);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onReloadClient) {
          await onReloadClient({ clientId });
        }
      } else {
        setSaveError(result.error || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditKey = () => {
    setIsEditingKey(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditKey = () => {
    setIsEditingKey(false);
    setFormData({ ...formData, key: client?.Clave || '' });
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSaveKey = async () => {
    setSaveError('');
    setSaveSuccess(false);

    // an empty save would blank the admin's locker login key on the backend
    if (!formData.key.trim()) {
      setSaveError(translate('dashboard.clients.detail.overview.errors.emptyField') || 'This field cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        birthDate: formData.birthDate,
        pais: formData.pais.trim(),
        estado: formData.estado.trim(),
        ciudad: formData.ciudad.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        externalId: formData.externalId.trim(),
        accessCode: formData.accessCode.trim(),
        hashKey: formData.hashKey.trim(),
        // key es la Clave de login del admin del locker; solo se envía en este flujo
        key: formData.key.trim(),
        acc: isAccessible,
      };
      const formattedData = formatClientData ? formatClientData(payload) : payload;

      const result = await onUpdateClient({
        typeUpdate: 'EDIT_INFO_CLIENT',
        ...formattedData
      });

      if (result.success) {
        setSaveSuccess(true);
        setIsEditingKey(false);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onReloadClient) {
          await onReloadClient({ clientId });
        }
      } else {
        setSaveError(result.error || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.clients.detail.overview.errors.saveError') || 'Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.overviewContainer}>
      {(saveError || saveSuccess) && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem 1rem', 
          borderRadius: '8px',
          backgroundColor: saveSuccess ? '#d4edda' : '#f8d7da',
          color: saveSuccess ? '#155724' : '#721c24',
          border: `1px solid ${saveSuccess ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {saveSuccess ? (translate('dashboard.clients.detail.overview.messages.saveSuccess') || 'Saved successfully') : `✗ ${saveError}`}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 mb-4">
        {/* Card 1: Información Básica */}
        <div>
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <FiUser size={20} />
                <span>{translate('dashboard.clients.detail.personalInfo.basicInfo.title') || translate('dashboard.userprofile.personal.title')}</span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
            // headerActions deshabilitado temporalmente para evitar edición
            // headerActions={
            //   !isEditing ? (
            //     <Button 
            //       variant="ghostOutline" 
            //       onClick={handleStartEdit}
            //       leftIcon={<FaIcons.FaEdit />}
            //       className={styles.sectionEditButton}
            //     >
            //       {translate('global.edit')}
            //     </Button>
            //   ) : (
            //     <div style={{ display: 'flex', gap: '0.5rem' }}>
            //       <Button 
            //         variant="primary" 
            //         onClick={handleSave}
            //         leftIcon={<FaIcons.FaSave />}
            //         className={styles.sectionEditButton}
            //         loading={isSaving}
            //         disabled={isSaving}
            //       >
            //         {translate('global.save')}
            //       </Button>
            //       <Button 
            //         variant="ghostOutline" 
            //         onClick={handleCancelEdit}
            //         className={styles.sectionEditButton}
            //         disabled={isSaving}
            //       >
            //         {translate('global.cancel')}
            //       </Button>
            //     </div>
            //   )
            // }
          >
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  <FiUser className={styles.infoIcon} />
                  {translate('global.name')}
                </span>
                {isEditing ? (
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={translate('global.name')}
                    fullWidth
                    required
                  />
                ) : (
                  <span className={styles.infoValue}>{client?.Nombre || 'N/A'}</span>
                )}
              </div>
              
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  {translate('global.lastNames')}
                </span>
                {isEditing ? (
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder={translate('global.lastNames')}
                    fullWidth
                    required
                  />
                ) : (
                  <span className={styles.infoValue}>{client?.Apellidos || 'N/A'}</span>
                )}
              </div>

              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  <FiMail className={styles.infoIcon} />
                  {translate('global.email')}
                </span>
                <span className={styles.infoValue}>{client?.Email || 'N/A'}</span>
              </div>

              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  <FiPhone className={styles.infoIcon} />
                  {translate('global.phone')}
                </span>
                {isEditing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={translate('global.phone')}
                    fullWidth
                  />
                ) : (
                  <span className={styles.infoValue}>{client?.Telefono || 'N/A'}</span>
                )}
              </div>

              {client?.Sexo && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.gender')}
                  </span>
                  {isEditing ? (
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #d2d2d7', width: '100%' }}
                    >
                      <option value="H">{translate('global.male')}</option>
                      <option value="M">{translate('global.female')}</option>
                    </select>
                  ) : (
                    <span className={styles.infoValue}>
                      {getNameGender(client?.Sexo)}
                    </span>
                  )}
                </div>
              )}

              {client?.FechaNacimiento && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    <FiCalendar className={styles.infoIcon} />
                    {translate('dashboard.userprofile.personal.birthdate_label')}
                  </span>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>
                      {getFormatDate(client.FechaNacimiento)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </ContentCard>
        </div>

        {/* Card 2: Información de Dirección */}
        {!verifyHideCountryItems(["us"]) && (
          <div>
            <ContentCard 
              title={
                <div className={styles.cardTitle}>
                  <FiMapPin size={20} />
                  <span>{translate('dashboard.clients.detail.personalInfo.addressInfo.title') || translate('global.address')}</span>
                </div>
              }
              padding="md"
              className={styles.infoCard}
              // headerActions deshabilitado temporalmente para evitar edición
              // headerActions={
              //   !isEditing ? (
              //     <Button 
              //       variant="ghostOutline" 
              //       onClick={handleStartEdit}
              //       leftIcon={<FaIcons.FaEdit />}
              //       className={styles.sectionEditButton}
              //     >
              //       {translate('global.edit')}
              //     </Button>
              //   ) : (
              //     <div style={{ display: 'flex', gap: '0.5rem' }}>
              //       <Button 
              //         variant="primary" 
              //         onClick={handleSave}
              //         leftIcon={<FaIcons.FaSave />}
              //         className={styles.sectionEditButton}
              //         loading={isSaving}
              //         disabled={isSaving}
              //       >
              //         {translate('global.save')}
              //       </Button>
              //       <Button 
              //         variant="ghostOutline" 
              //         onClick={handleCancelEdit}
              //         className={styles.sectionEditButton}
              //         disabled={isSaving}
              //       >
              //         {translate('global.cancel')}
              //       </Button>
              //     </div>
              //   )
              // }
            >
              <div className={styles.infoList}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    <FiGlobe className={styles.infoIcon} />
                    {translate('global.Country')}
                  </span>
                  {isEditing ? (
                    <Input
                      value={formData.pais}
                      onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                      placeholder={translate('global.Country')}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>{client?.Pais || 'N/A'}</span>
                  )}
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.StateLocation')}
                  </span>
                  {isEditing ? (
                    <Input
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      placeholder={translate('global.StateLocation')}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>{client?.Estado || 'N/A'}</span>
                  )}
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.City')}
                  </span>
                  {isEditing ? (
                    <Input
                      value={formData.ciudad}
                      onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                      placeholder={translate('global.City')}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>{client?.Ciudad || 'N/A'}</span>
                  )}
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.Address1')}
                  </span>
                  {isEditing ? (
                    <Input
                      value={formData.address1}
                      onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                      placeholder={translate('global.Address1')}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>{client?.Address1 || 'N/A'}</span>
                  )}
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.Address2')}
                  </span>
                  {isEditing ? (
                    <Input
                      value={formData.address2}
                      onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                      placeholder={translate('global.Address2')}
                      fullWidth
                    />
                  ) : (
                    <span className={styles.infoValue}>{client?.Address2 || 'N/A'}</span>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        )}
      </div>

      {/* Fila 2: Card 3 (Seguridad) y Card 4 (Información Adicional) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 mb-4">
        {/* Card 3: Información de Seguridad */}
        <div>
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <FiKey size={20} />
                <span>{translate('dashboard.clients.detail.personalInfo.security.title') || translate('global.security')}</span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
            headerActions={
              canEditAccessCode ? (
                !isEditingAccessCode ? (
                  <Button
                    variant="ghostOutline"
                    onClick={handleStartEditAccessCode}
                    leftIcon={<FaIcons.FaEdit />}
                    className={styles.sectionEditButton}
                  >
                    {translate('global.edit')}
                  </Button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="primary"
                      onClick={handleSaveAccessCode}
                      leftIcon={<FaIcons.FaSave />}
                      className={styles.sectionEditButton}
                      loading={isSaving}
                      disabled={isSaving}
                    >
                      {translate('global.save')}
                    </Button>
                    <Button
                      variant="ghostOutline"
                      onClick={handleCancelEditAccessCode}
                      className={styles.sectionEditButton}
                      disabled={isSaving}
                    >
                      {translate('global.cancel')}
                    </Button>
                  </div>
                )
              ) : canEditKey ? (
                !isEditingKey ? (
                  <Button
                    variant="ghostOutline"
                    onClick={handleStartEditKey}
                    leftIcon={<FaIcons.FaEdit />}
                    className={styles.sectionEditButton}
                  >
                    {translate('global.edit')}
                  </Button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="primary"
                      onClick={handleSaveKey}
                      leftIcon={<FaIcons.FaSave />}
                      className={styles.sectionEditButton}
                      loading={isSaving}
                      disabled={isSaving}
                    >
                      {translate('global.save')}
                    </Button>
                    <Button
                      variant="ghostOutline"
                      onClick={handleCancelEditKey}
                      className={styles.sectionEditButton}
                      disabled={isSaving}
                    >
                      {translate('global.cancel')}
                    </Button>
                  </div>
                )
              ) : null
            }
          >
            <div style={{ padding: '1rem' }}>
              <div className="flex flex-wrap">
                <div className="lg:w-full md:w-full sm:w-full mb-3">
                  <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                    <span className={styles.infoLabel} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {translate('global.ExternalID')}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyClipboard(client?.UserIdGL)}
                        className={styles.copyButton}
                      >
                        <FaIcons.FaCopy size={14} />
                      </Button>
                    </span>
                    {isEditing ? (
                      <Input
                        value={formData.externalId}
                        onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                        placeholder={translate('global.ExternalID')}
                        fullWidth
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                        <span className={styles.infoValue} style={{ textAlign: 'left', flex: 1 }}>{client?.UserIdGL || 'N/A'}</span>
                        {copied && <span style={{ color: '#28a745', fontSize: '12px' }}>{translate('global.copied')}</span>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="lg:w-full md:w-full sm:w-full mb-3">
                  <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                    <span className={styles.infoLabel}>
                      {translate('global.AccessCode')}
                    </span>
                    <div style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
                      {isEditingAccessCode ? (
                        <>
                          <Input
                            type={showAccessCode ? 'text' : 'password'}
                            value={formData.accessCode}
                            onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                            placeholder={translate('global.AccessCode')}
                            fullWidth
                          />
                          <Button
                            variant="ghost"
                            onClick={toggleShowAccessCode}
                            style={{ minWidth: 'auto', padding: '0.5rem' }}
                          >
                            {showAccessCode ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Input
                            type={showAccessCode ? 'text' : 'password'}
                            value={client?.AccessCode || ''}
                            disabled
                            fullWidth
                          />
                          <Button
                            variant="ghost"
                            onClick={toggleShowAccessCode}
                            style={{ minWidth: 'auto', padding: '0.5rem' }}
                          >
                            {showAccessCode ? <FiEyeOff /> : <FiEye />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="lg:w-full md:w-full sm:w-full mb-3">
                  <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                    <span className={styles.infoLabel}>
                      {translate('global.HashKey')}
                    </span>
                    <div style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={formData.hashKey}
                          onChange={(e) => setFormData({ ...formData, hashKey: e.target.value })}
                          placeholder={translate('global.HashKey')}
                          fullWidth
                        />
                      ) : (
                        <>
                          <Input
                            type="password"
                            value={client?.HashKey || ''}
                            disabled
                            fullWidth
                          />
                          <Button
                            variant="ghost"
                            onClick={() => setIsOpenModal(true)}
                            style={{ minWidth: 'auto', padding: '0.5rem' }}
                          >
                            <ImQrcode />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="lg:w-full md:w-full sm:w-full mb-3">
                    <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                      <span className={styles.infoLabel}>
                        {translate('global.Key')}
                      </span>
                      <div style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
                        {isEditingKey ? (
                          <>
                            <Input
                              type={showKey ? 'text' : 'password'}
                              value={formData.key}
                              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                              placeholder={translate('global.Key')}
                              fullWidth
                            />
                            <Button
                              variant="ghost"
                              onClick={toggleShowKey}
                              style={{ minWidth: 'auto', padding: '0.5rem' }}
                            >
                              {showKey ? <FiEyeOff /> : <FiEye />}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Input
                              type={showKey ? 'text' : 'password'}
                              value={client?.Clave || ''}
                              disabled
                              fullWidth
                            />
                            <Button
                              variant="ghost"
                              onClick={toggleShowKey}
                              style={{ minWidth: 'auto', padding: '0.5rem' }}
                            >
                              {showKey ? <FiEyeOff /> : <FiEye />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="lg:w-full md:w-full sm:w-full mb-3">
                  <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                    <span className={styles.infoLabel}>
                      {translate('global.Accesibility')}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={isEditing ? isAccessible : (client?.Acc || false)}
                        onChange={(e) => {
                          if (isEditing) {
                            setIsAccessible(e.target.checked);
                            setFormData({ ...formData, acc: e.target.checked });
                          }
                        }}
                        disabled={!isEditing}
                        style={{ width: '18px', height: '18px', cursor: isEditing ? 'pointer' : 'not-allowed' }}
                      />
                      <span style={{ color: isEditing ? (isAccessible ? '#28a745' : '#dc3545') : (client?.Acc ? '#28a745' : '#dc3545'), fontWeight: 'bold' }}>
                        {isEditing ? (isAccessible ? translate('global.yes') : translate('global.no')) : (client?.Acc ? translate('global.yes') : translate('global.no'))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ContentCard>
        </div>

        {/* Card 4: Información Adicional (Solo Lectura) */}
        <div>
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <FiInfo size={20} />
                <span>{translate('global.additionalInfo')}</span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
          >
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  {translate('global.status')}
                </span>
                <Badge 
                  variant={getStatusVariant(client?.IdEstatus)} 
                  showIcon
                >
                  {getNameStatusClients(client?.IdEstatus)}
                </Badge>
              </div>

              {client?.Usuario && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.username')}
                  </span>
                  <span className={styles.infoValue}>{client.Usuario}</span>
                </div>
              )}

              {client?.IdConfRoles && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('dashboard.companies.detail.clients.columns.role')}
                  </span>
                  <span className={styles.infoValue}>
                    <Badge variant="neutral" icon={getClientConfRoleInfo(client.IdConfRoles).icon} showIcon={true} size="sm">
                      {getClientConfRoleInfo(client.IdConfRoles).label}
                    </Badge>
                  </span>
                </div>
              )}

              {client?.IdTipoCliente === 7 && client?.IdRoleBli && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.role')} BLI
                  </span>
                  <span className={styles.infoValue}>
                    {getNameRoleBli(client.IdRoleBli)}
                  </span>
                </div>
              )}

              {client?.IdTipoPlan && client?.IdTipoCliente !== 7 && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    {translate('global.type')} {translate('global.plan')}
                  </span>
                  <span className={styles.infoValue}>
                    {getNameTipoPlanCliente(client.IdTipoPlan)}
                  </span>
                </div>
              )}

              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  <FiCalendar className={styles.infoIcon} />
                  {translate('global.createdThe')}
                </span>
                <span className={styles.infoValue}>
                  {client?.FechaCreacion ? getFormatDate(client.FechaCreacion) : 'N/A'}
                </span>
              </div>
            </div>
          </ContentCard>
        </div>
      </div>

      {/* Company Info for BLI Users */}
      {verifyHideCountryItems(["us"]) && client?.IdTipoCliente === 7 && (
        <div className="flex flex-wrap">
          <div className="w-full mb-4">
            <ContentCard 
              title={
                <div className={styles.cardTitle}>
                  <FaIcons.FaRegBuilding size={20} />
                  <span>{translate('global.companyInfo')}</span>
                </div>
              }
              padding="md"
              className={styles.infoCard}
            >
              <div style={{ padding: '1rem' }}>
                <div className="flex flex-wrap">
                  <div className="lg:w-4/12 md:w-6/12 sm:w-full mb-3">
                    <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                      <span className={styles.infoLabel}>
                        {translate('global.name')}
                      </span>
                      <span className={styles.infoValue} style={{ textAlign: 'left', width: '100%' }}>
                        {client?.CompanyLastName} {client?.CompanyName}
                      </span>
                    </div>
                  </div>

                  <div className="lg:w-4/12 md:w-6/12 sm:w-full mb-3">
                    <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                      <span className={styles.infoLabel}>
                        {translate('global.hoursExpire')}
                      </span>
                      <span className={styles.infoValue} style={{ textAlign: 'left', width: '100%' }}>
                        {client?.HoursXExpire} h
                      </span>
                    </div>
                  </div>

                  <div className="lg:w-4/12 md:w-6/12 sm:w-full mb-3">
                    <div className={styles.infoItem} style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '0.5rem 0' }}>
                      <span className={styles.infoLabel}>
                        {translate('global.dateCreated')}
                      </span>
                      <span className={styles.infoValue} style={{ textAlign: 'left', width: '100%' }}>
                        {client?.CompanyDateCreate ? getFormatDate(client.CompanyDateCreate) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
      )}

      <ModalContent
        title={translate('global.codeQR')}
        body={<QRCode value={client?.HashKey || ''} />}
        footer={<Button variant="primary" onClick={() => setIsOpenModal(false)}>{translate('global.close')}</Button>}
        isOpenModal={isOpenModal}
        onHide={() => setIsOpenModal(false)}
      />
    </div>
  );
};

export default PersonalInfoTab;
